// Client-side port of the on-chain Fireball math (crates/fireball-math/src/lib.rs).
// The Rust contract is the AUTHORITY — these functions must stay byte-faithful to
// it (drift-guarded in fireballMath.test.js). Mirrors the SHIPPED contract, which
// has drifted from the original spec:
//   • pool of 64, pick 6 distinct numbers, 6 balls drawn WITHOUT replacement
//   • paying tiers 3/4/5 only (t0/1/2 pay nothing); a 6-match folds into tier 5
//   • stake split 10/8/67/15 % → pool_3/pool_4/pool_5/fee  (currency = DIESEL)
//   • FIXED ticket price: every bet must equal /ticket_price → equal split per pick
//
// Amounts are DIESEL base units (u128 on-chain) → we use BigInt to avoid float drift.

// ── Game constants (immutable on-chain) ──────────────────────────────────────
export const K = 64;            // number pool [0..63]
export const PICK = 6;          // numbers per ticket  (TICKET_SIZE)

// Bet routing in basis points (sum = 10000). There is no tier-6 pool: the former
// jackpot allocation is folded into POOL_5 (a 6-match is paid as the top tier).
export const POOL_3_BPS = 1000n; // 10%
export const POOL_4_BPS = 800n;  //  8%
export const POOL_5_BPS = 6700n; // 67% (top prize; folded 6-match)
export const FEE_BPS = 1400n;    // 14% protocol fee
export const KEEPER_BPS = 100n;  //  1% keeper draw reward
export const BPS_DENOM = 10000n;

// On-chain ids (Alkanes block:tx). Game/vault/position-template live at block 4.
export const FIREBALL_IDS = {
  DIESEL: { block: 2, tx: 0 },
  GAME: { block: 4, tx: 0x101 },          // 4:257
  POSITION_TEMPLATE: { block: 4, tx: 0x100 }, // 4:256
  VAULT: { block: 4, tx: 777 },           // 4:777 (deployed)
};

// Ticket price is fixed on-chain (contract /ticket_price slot), so we treat it as
// a constant rather than reading it every render. DIESEL has 8 decimals → 0.069.
export const DIESEL_DECIMALS = 1e8;
export const TICKET_PRICE = 6_900_000n;            // DIESEL base units

// Paying tiers, top → bottom. Tier 5 is the top prize (absorbs any 6-match).
// odds = "1 in N" where N = round(C(64,6) / (C(6,t)·C(58,6-t))) — hypergeometric,
// without replacement (see oddsOneIn / the contract's draw_ball).
export const PAYING_TIERS = [5, 4, 3];

// ── Ticket validation ────────────────────────────────────────────────────────
// 6 distinct numbers in [0..63], returned sorted ascending (matches Ticket::
// validate_and_sort). Throws on invalid input.
export function validateAndSort(nums) {
  if (!Array.isArray(nums) || nums.length !== PICK) {
    throw new Error(`ticket must have exactly ${PICK} numbers`);
  }
  const a = nums.map((n) => {
    const v = Number(n);
    if (!Number.isInteger(v) || v < 0 || v >= K) {
      throw new Error('ticket number out of range [0..63]');
    }
    return v;
  }).sort((x, y) => x - y);
  for (let i = 1; i < PICK; i++) {
    if (a[i] === a[i - 1]) throw new Error('ticket numbers must be distinct');
  }
  return a;
}

// ── Pack / unpack (6 bits per number) ────────────────────────────────────────
// Mirrors fireball_math::pack — each element occupies 6 bits, low slot first.
// 6×6 = 36 bits exceeds JS 32-bit bitwise ops, so we use BigInt. Returns BigInt.
export function packKey(sortedNums) {
  let key = 0n;
  sortedNums.forEach((n, i) => {
    key |= (BigInt(n & 0x3f)) << BigInt(i * 6);
  });
  return key;
}

// Inverse of packKey for a full 6-number ticket. `packed` may be BigInt | number
// | string (the contract returns it as a u128 in ViewPosition).
export function unpackTicket(packed) {
  const p = BigInt(packed);
  const out = [];
  for (let i = 0; i < PICK; i++) {
    out.push(Number((p >> BigInt(i * 6)) & 0x3fn));
  }
  return out;
}

// ── Match count (set intersection, balls deduped) ────────────────────────────
export function matchCount(ticketNums, balls) {
  const drawn = new Set(balls.map(Number));
  let t = 0;
  for (const n of ticketNums) if (drawn.has(Number(n))) t += 1;
  return t;
}

// Tier a ticket falls into for PAYOUT purposes: match count clamped to 5 (a
// 6-match is paid as the top tier). 0/1/2 = no prize.
export function payoutTier(ticketNums, balls) {
  return Math.min(matchCount(ticketNums, balls), 5);
}

// ── Stake split (mirrors fireball_math::split_bet) ───────────────────────────
// Pool + keeper shares are floor(bet·bps/10000); the fee absorbs the rounding
// dust so the parts sum to exactly `bet`. `bet` is DIESEL base units → BigInt.
// Verified against a live 4:257 ticket: 6,900,000 → 690k/552k/4.623M/69k/966k.
export function splitBet(bet) {
  const b = BigInt(bet);
  const to3 = (b * POOL_3_BPS) / BPS_DENOM;
  const to4 = (b * POOL_4_BPS) / BPS_DENOM;
  const to5 = (b * POOL_5_BPS) / BPS_DENOM;
  const toKeeper = (b * KEEPER_BPS) / BPS_DENOM;
  const toFee = b - (to3 + to4 + to5 + toKeeper);
  return { to3, to4, to5, toKeeper, toFee };
}

// ── Pari-mutuel payout (mirrors fireball_math::tier_payout) ───────────────────
// One winner's share of an immutable per-round snapshot. With a fixed ticket
// price every winner's bet is equal, so this degenerates to pool/count — but we
// keep the general form to match the contract and support previews from
// ViewWinners (pool_t_snapshot, winners_t_bet).
export function tierPayout(snap, bet, winnersBet) {
  const w = BigInt(winnersBet);
  if (w === 0n) return 0n;
  return (BigInt(snap) * BigInt(bet)) / w;
}

// ── Odds: hypergeometric P(exactly t matches), as "1 in N" ───────────────────
function comb(n, k) {
  if (k < 0 || k > n) return 0n;
  k = Math.min(k, n - k);
  let num = 1n, den = 1n;
  for (let i = 0; i < k; i++) {
    num *= BigInt(n - i);
    den *= BigInt(i + 1);
  }
  return num / den;
}

// N such that P(t) ≈ 1/N. Without replacement: C(6,t)·C(58,6-t) / C(64,6).
export function oddsOneIn(t) {
  const favorable = comb(PICK, t) * comb(K - PICK, PICK - t);
  if (favorable === 0n) return Infinity;
  const total = comb(K, PICK); // C(64,6) = 74,974,368
  return Number(total) / Number(favorable);
}

// Probability of matching AT LEAST t (used for the "top prize = 5+" headline).
export function oddsOneInAtLeast(t) {
  let favorable = 0n;
  for (let j = t; j <= PICK; j++) favorable += comb(PICK, j) * comb(K - PICK, PICK - j);
  if (favorable === 0n) return Infinity;
  return Number(comb(K, PICK)) / Number(favorable);
}
